<script setup lang="ts">
import { ElMessage, ElMessageBox } from "element-plus";
const { t } = useI18n();
const { width } = useWindowSize();
const CurrencyStore = useCurrencyStore();
const { fiatCurrencies } = storeToRefs(CurrencyStore);
const emit = defineEmits<{
  (e: "close", optionId?: number): void;
}>();
interface CreateProps {
  editingOption?: any;
  filterCurrency?: string;
  filterBPO?: number[];
}
const store = usePaymentOptionsStore();
const { addMyOption, editMyOption } = store;
const { baseOptionsGrouped, allBaseOptions } = storeToRefs(store);
const prop = defineProps<CreateProps>();
const show = ref(true);
const onClose = () => {
  ElMessageBox.confirm(t("c-payment-option-close-dialog-confirm"))
    .then(() => {
      emit("close");
    })
    .catch(() => {});
  return false;
};
let bpoCurrency: string | undefined;
if (prop.filterBPO) {
  const fildBpo = allBaseOptions.value.find((option) => {
    return option.bpo_id === prop.filterBPO?.[0];
  });
  bpoCurrency = fildBpo?.currency;
}
const currencySelected = ref(prop.filterCurrency || bpoCurrency);
const formRef = ref();
const form = reactive({
  paymentMethod: prop.editingOption?.base_id || null,
  textarea: prop.editingOption?.description || "",
});
const descriptionText = ref("");
const rules = computed(() => ({
  paymentMethod: [
    {
      required: true,
      message: t("c-payment-option-validate-payment-option"),
      trigger: "change",
    },
  ],
  textarea: [
    {
      required: true,
      message: t("c-payment-option-validate-description"),
      trigger: "change",
    },
    {
      validator: (_: any, _v: string, callback: any) => {
        if (descriptionText.value === "") {
          callback(new Error(t("c-payment-option-validate-description")));
        }
        callback();
      },
      trigger: "change",
    },
  ],
}));
const loading = ref(false);
const proceed = async () => {
  if (loading.value) return;
  let valid = false;
  try {
    valid = await formRef.value.validate();
  } catch (a) {}
  if (!valid) return;
  loading.value = true;
  let createdOptionId: number;
  try {
    if (prop.editingOption) {
      await editMyOption(prop.editingOption.upo_id, form.textarea);
      createdOptionId = prop.editingOption.upo_id;
    } else {
      createdOptionId = (await addMyOption(form.paymentMethod, form.textarea))
        .createdUpoId;
    }
    emit("close", createdOptionId);
  } catch (e: any) {
    ElMessage.error(e?.data?.data?.message || t("c-payment-option-save-error"));
  }
  loading.value = false;
};
const filtredOptionGrouped = computed(() => {
  if (!prop.filterBPO && !currencySelected.value)
    return baseOptionsGrouped.value;
  if (prop.filterBPO) {
    const filtredGroups = baseOptionsGrouped.value.filter((group) => {
      return group.children.some((item) =>
        prop.filterBPO?.includes(item.bpo_id)
      );
    });
    const filterdChildren = filtredGroups.map((group) => {
      const children = group.children.filter((item) =>
        prop.filterBPO?.includes(item.bpo_id)
      );
      return { ...group, children };
    });
    currencySelected.value = filterdChildren[0].children[0].currency;
    return filterdChildren;
  } else {
    const filtredGroups = baseOptionsGrouped.value.filter((group) => {
      return group.children.some(
        (item) => item.currency === currencySelected.value
      );
    });
    const filterdChildren = filtredGroups.map((group) => {
      const children = group.children.filter(
        (item) => item.currency === currencySelected.value
      );
      return { ...group, children };
    });
    return filterdChildren;
  }
});
const onSelectingCurrency = async () => {
  const temp = form.paymentMethod;
  form.paymentMethod = null;
  if (!temp) {
    setTimeout(() => {
      formRef.value.clearValidate();
    }, 1);
  }
};
const textareaRef = ref();
</script>

<template>
  <el-dialog
    :title="`${
      editingOption ? t('c-payment-option-edit') : t('c-payment-option-new')
    }`"
    v-model="show"
    width="600px"
    :fullscreen="width < 650"
    :before-close="onClose"
    :destroy-on-close="true"
  >
    <el-alert
      type="warning"
      class="mb-2"
      v-if="filterCurrency"
      :closable="false"
    >
      {{
        t("c-payment-option-show-currency-methods", {
          currency: filterCurrency,
        })
      }}
    </el-alert>
    <el-alert type="warning" class="mb-2" v-if="filterBPO" :closable="false">
      {{ t("c-payment-option-show-specified-options") }}
    </el-alert>
    <p v-if="editingOption">{{ t("c-payment-option-editing-method") }}</p>
    <p v-else>{{ t("c-payment-option-select-method") }}</p>

    <el-form
      :model="form"
      :rules="rules"
      ref="formRef"
      @submit.prevent="() => {}"
    >
      <el-form-item prop="paymentMethod">
        <el-select
          v-model="currencySelected"
          :disabled="!!prop.filterCurrency || !!prop.filterBPO"
          size="large"
          class="mr-1 mb-2"
          filterable
          :placeholder="t('p-trade-placeholder-type')"
          @change="onSelectingCurrency"
          :style="width < 690 ? 'width: 100%' : 'width: 230px'"
        >
          <el-option
            v-for="item in fiatCurrencies"
            :key="item.name"
            :label="item.name"
            :value="item.name"
          />
        </el-select>
        <el-select
          :disabled="!!editingOption || !currencySelected"
          v-model="form.paymentMethod"
          :placeholder="t('c-payment-option-select-method-placeholder')"
          filterable
          class="mb-2"
          size="large"
          :style="width < 690 ? 'width: 100%' : 'width: 230px'"
        >
          <el-option-group
            v-for="group in filtredOptionGrouped"
            :key="group.bpo_id"
            :label="group.name"
          >
            <el-option
              v-for="item in group.children"
              :key="item.bpo_id"
              :label="`${item.name} - ${item.currency}`"
              :value="item.bpo_id"
            />
          </el-option-group>
        </el-select>
      </el-form-item>
      <el-form-item prop="textarea" ref="textareaRef">
        <div class="block line-height-normal">
          <p class="mt-5">{{ t("c-payment-option-description") }}</p>
          <div class="mb-2">
            <el-alert :closable="false">
              {{ t("c-payment-option-description-info") }}
            </el-alert>
          </div>

          <TipTap
            v-model="form.textarea"
            @update:text="
              descriptionText = $event;
              textareaRef.validate();
            "
            :placeholder="t('c-payment-option-description-placeholder')"
          ></TipTap>
        </div>
      </el-form-item>
    </el-form>
    <el-divider />
    <div class="text-right">
      <el-button
        round
        size="large"
        type="primary"
        class="new-option-btn"
        @click="proceed"
        :disabled="loading"
        :loading="loading"
      >
        {{ t("c-payment-option-save") }}
      </el-button>
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
.el-alert.mb-2 {
  margin-bottom: 0.5rem;
}
</style>
